import { useCallback, useEffect, useState } from "react";
import { UserCircle, FloppyDisk, Crown, MapTrifold, PenNib } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ICON = { owner: Crown, field_executive: MapTrifold, report_writer: PenNib };

export default function ValuerProfiles() {
  const [rows, setRows] = useState(null);
  const [creds, setCreds] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/team/valuer-profiles").then((r) => {
    setRows(r.data);
    setCreds(Object.fromEntries(r.data.map((m) => [m.id, { registration_no: "", empanelled_banks: "", qualification: "", ...(m.credentials || {}) }])));
  }).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try { await api.put("/settings/valuer_profiles", creds); toast.success("Credentials saved"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!rows) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading valuer profiles…</div>;

  const totalAssigned = rows.reduce((s, r) => s + r.assigned, 0);
  const tatRows = rows.filter((r) => r.avg_tat_days != null);

  return (
    <div className="space-y-6" data-testid="valuer-profiles-page">
      <PageHeader
        overline="Team" title="Valuer Profiles"
        subtitle="Credentials, workload and performance for client-facing staff"
        action={<Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-credentials-btn"><FloppyDisk size={16} className="mr-1" /> {busy ? "Saving…" : "Save Credentials"}</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Team members" value={rows.length} testid="stat-vp-members" />
        <Stat label="Assignments" value={totalAssigned} testid="stat-vp-assigned" />
        <Stat label="Open work" value={rows.reduce((s, r) => s + r.open, 0)} tone="text-amber-600" testid="stat-vp-open" />
        <Stat label="Avg TAT" value={tatRows.length ? `${(tatRows.reduce((s, r) => s + r.avg_tat_days, 0) / tatRows.length).toFixed(1)} d` : "—"} testid="stat-vp-tat" />
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={UserCircle}>No team members yet.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((m) => {
            const Icon = ICON[m.role] || UserCircle;
            const c = creds[m.id] || {};
            return (
              <Card key={m.id} className={`p-5 border-border ${m.is_active === false ? "opacity-60" : ""}`} data-testid={`valuer-${m.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-primary/5 flex items-center justify-center shrink-0"><Icon size={22} weight="duotone" className="text-primary" /></div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-primary truncate">{m.name}</div>
                    <div className="text-xs text-accent">{ROLE_LABELS[m.role] || m.role}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{m.email}</div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                  {[
                    { l: "Assigned", v: m.assigned, t: "text-primary" },
                    { l: "Done", v: m.completed, t: "text-emerald-600" },
                    { l: "Open", v: m.open, t: "text-amber-600" },
                    { l: "Avg TAT", v: m.avg_tat_days != null ? `${m.avg_tat_days}d` : "—", t: "text-primary" },
                  ].map((x) => (
                    <div key={x.l} className="rounded-md bg-muted border border-border py-2">
                      <div className={`font-display font-bold text-lg tabular ${x.t}`}>{x.v}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{x.l}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Revision rate</span>
                  <span className={m.revision_rate > 15 ? "text-rose-600 font-semibold" : "text-primary"}>{m.revision_rate}%</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full transition-[width] duration-500 ${m.revision_rate > 15 ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, m.revision_rate)}%` }} />
                </div>

                <div className="mt-4 pt-4 border-t border-border space-y-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Credentials</div>
                  <div className="space-y-1.5"><Label className="text-xs">Registered Valuer No.</Label>
                    <Input className="h-8 text-xs" value={c.registration_no || ""} onChange={(e) => setCreds({ ...creds, [m.id]: { ...c, registration_no: e.target.value } })} placeholder="IBBI / CBDT registration" data-testid={`vp-reg-${m.id}`} />
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Qualification</Label>
                    <Input className="h-8 text-xs" value={c.qualification || ""} onChange={(e) => setCreds({ ...creds, [m.id]: { ...c, qualification: e.target.value } })} placeholder="e.g. B.E. Civil, MIV" data-testid={`vp-qual-${m.id}`} />
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Bank-wise Empanelment</Label>
                    <Input className="h-8 text-xs" value={c.empanelled_banks || ""} onChange={(e) => setCreds({ ...creds, [m.id]: { ...c, empanelled_banks: e.target.value } })} placeholder="SBI, HDFC, PNB…" data-testid={`vp-banks-${m.id}`} />
                  </div>
                </div>

                {m.last_login_at && <div className="text-[10px] text-muted-foreground mt-3">Last signed in {new Date(m.last_login_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
